import { useEffect, useRef, useCallback, useState } from 'react'

/**
 * useGlobalScanner – global keyboard-based scanner listener
 *
 * Bluetooth / USB barcode scanners behave like keyboards: they fire rapid
 * keydown events and finish with Enter.  This hook captures those bursts
 * while ignoring normal user typing.
 *
 * @param {Function} onScan  – called with the full scanned string
 * @param {Object}   options
 * @param {number}   options.maxDelay    – max ms between keystrokes (default 50)
 * @param {number}   options.minLength   – minimum chars to be considered a scan (default 3)
 * @param {boolean}  options.enabled     – toggle the listener on/off (default true)
 */
// Shared state to ensure multiple useGlobalScanner instances don't 
// double-dispatch the same physical scan event in the same burst.
let lastEmittedScanData = { value: '', time: 0 }

export const useGlobalScanner = (onScan, options = {}) => {
  // Defaults tuned for PANDA PRJ-BT444 Bluetooth HID mode.
  // Set to 500ms so it glues fragments together without waiting too long if 'Enter' is missing.
  const storedMax = Number(localStorage.getItem('PMB_scannerMaxDelay'))
  const savedMaxDelay = storedMax && storedMax >= 50 ? storedMax : 200
  const savedMinLength =
    Number(localStorage.getItem('PMB_scannerMinLength')) || 3
  const [isScanning, setIsScanning] = useState(false)
  const {
    maxDelay = savedMaxDelay,
    minLength = savedMinLength,
    enabled = true,
    isGlobal = false, // If true, this listener can be intercepted/prevented
  } = options

  const bufferRef = useRef('')
  const lastKeyTimeRef = useRef(0)
  const timeoutRef = useRef(null)
  const onScanRef = useRef(onScan)

  // Always keep the latest callback without re-registering the listener
  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  const resetBuffer = useCallback(() => {
    bufferRef.current = ''
    lastKeyTimeRef.current = 0
    setIsScanning(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const isInputFocused = useCallback(() => {
    const el = document.activeElement
    if (!el) return false
    const tag = el.tagName.toLowerCase()
    const role = el.getAttribute('role') || ''
    const type = el.getAttribute('type') || ''

    if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      // Small exception: if it's a checkbox or radio, we can still scan
      if (type === 'checkbox' || type === 'radio') return false
      return true
    }

    if (el.isContentEditable) return true
    if (role === 'combobox' || role === 'textbox') return true

    return false
  }, [])

  const emitScan = useCallback(() => {
    const value = bufferRef.current.trim()
    const now = Date.now()

    if (value.length >= minLength) {
      // Coordination: check if this burst was already dispatched by another hook instance
      let scanEvent
      const isDuplicate = lastEmittedScanData.value === value && (now - lastEmittedScanData.time < 100)
      
      if (!isDuplicate) {
        lastEmittedScanData = { value, time: now }
        scanEvent = new CustomEvent('pmb:scan', {
          detail: { value },
          cancelable: true,
          bubbles: true
        })
        document.dispatchEvent(scanEvent)
      } else {
        // If duplicate, we still need to know if the previously dispatched event was prevented
        // But since dispatchEvent is synchronous, this is tricky. 
        // For simplicity, we'll assume the same preventDefault state applies or just use a shared 'handled' flag.
      }

      // If this is a global listener, we only proceed if the event was NOT prevented by a local listener.
      // We use a shared property on the event or a separate check.
      
      // Re-fetch the event status if it's a duplicate or use the fresh one
      const finalEvent = scanEvent || new CustomEvent('pmb:scan-check') // dummy if duplicate
      
      if (onScanRef.current) {
        const callback = onScanRef.current
        
        // Local listeners always get their callback. 
        // Global listeners only get it if no one suppressed it.
        // We detect suppression via event.defaultPrevented.
        
        const shouldExecute = !isGlobal || (scanEvent && !scanEvent.defaultPrevented)
        
        if (shouldExecute) {
          // Decouple from main keyboard thread
          setTimeout(() => callback(value, scanEvent), 0)
        }
      }
    }
    resetBuffer()
  }, [minLength, resetBuffer, isGlobal])

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e) => {
      // Use e.timeStamp for accuracy (independent of JS execution lag)
      const now = performance.now()
      const timeSinceLastKey = now - lastKeyTimeRef.current

      if (localStorage.getItem('PMB_scannerDebug')) {
        // Debug mode active
      }

      // If too much time elapsed since last keystroke, start fresh.
      if (lastKeyTimeRef.current > 0 && timeSinceLastKey > maxDelay) {
        resetBuffer()
      }

      const inputFocused = isInputFocused()
      const BURST_THRESHOLD = 50 // ms

      // If we are in an input field and the first key arrives, or if keys are coming slowly,
      // we assume it is manual typing and do NOT start a scanner buffer.
      const isSlowTyping = inputFocused && bufferRef.current.length === 0 && (lastKeyTimeRef.current === 0 || timeSinceLastKey > BURST_THRESHOLD)
      
      if (isSlowTyping) {
        // Let manual typing proceed normally
        return
      }

      // If Enter or Tab is pressed, consider scan complete.
      if (
        e.key === 'Enter' ||
        e.key === 'Tab' ||
        e.keyCode === 13 ||
        e.keyCode === 9
      ) {
        if (bufferRef.current.length > 0) {
          e.preventDefault()
          e.stopPropagation()
          emitScan()
        } else if (inputFocused) {
          return
        }
        return
      }

      // Ignore modifier keys
      if (
        e.key.length !== 1 ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        ['Control', 'Alt', 'Meta'].includes(e.key)
      ) {
        return
      }

      // Suppress native typing only if we are currently collecting a fast burst
      if (inputFocused && (bufferRef.current.length > 0 || timeSinceLastKey <= BURST_THRESHOLD)) {
         e.preventDefault()
         e.stopPropagation()
      }

      const wasEmpty = bufferRef.current.length === 0
      bufferRef.current += e.key
      lastKeyTimeRef.current = now

      // Only show "scanning" state if we see at least 2 characters in a fast burst
      // This prevents single accidental keypresses from showing the loading overlay
      if (bufferRef.current.length >= 2 && timeSinceLastKey <= BURST_THRESHOLD) {
        setIsScanning(true)
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        const timeSinceLastKeyNow = performance.now() - lastKeyTimeRef.current
        if (timeSinceLastKeyNow >= maxDelay && bufferRef.current.length >= minLength) {
          emitScan()
        } else if (timeSinceLastKeyNow >= maxDelay) {
          resetBuffer()
        }
      }, maxDelay)
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, maxDelay, minLength, isInputFocused, resetBuffer, emitScan])

  return { resetBuffer, isScanning }
}

export default useGlobalScanner
